import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Quotes, CheckCircle, Trophy, FilePdf, CalendarCheck } from "@phosphor-icons/react";
import { BrandMark } from "@/components/Brand";
import { CASE_STUDIES, PUBLISHED_CASE_STUDIES } from "@/lib/caseStudies";
import { DEMO_BOOKING_URL, DEMO_USES_EXTERNAL } from "@/lib/demoBooking";
import useSeo from "@/lib/useSeo";

const Mark = () => <BrandMark size={36} testid="brand-mark" tagline="AI-Driven Project Intelligence" />;

export default function CaseStudies() {
  useSeo({
    title: "Case studies · ARISSAI® · Real PMO wins with AI-powered project intelligence",
    description: "See how PMOs, transformation leads and FP&A teams use ARISSAI® to surface risks earlier, cut reporting time and defend executive decisions. Real metrics, no vanity quotes.",
    path: "/case-studies",
  });
  const upcoming = CASE_STUDIES.filter((c) => !c.published);

  return (
    <div className="min-h-screen bg-zinc-100 text-zinc-950">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-semibold hover:text-[#0020F5]" data-testid="back-home">
            <ArrowLeft size={14} weight="bold" /> Home
          </Link>
          <Mark />
          <Link to="/signup" className="h-10 px-5 inline-flex items-center text-sm font-bold bg-zinc-950 text-white hover:bg-zinc-800" data-testid="case-studies-signup-cta">
            Get started <ArrowRight size={14} weight="bold" className="ml-2" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-white border-b border-zinc-200 py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 inline-flex items-center gap-2">
            <Trophy size={12} weight="duotone" /> // Customer wins
          </div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-6xl mt-5 leading-[1.05]">
            Real teams. <br /><span className="text-[#0020F5]">Real outcomes.</span>
          </h1>
          <p className="text-zinc-700 mt-6 text-base sm:text-lg leading-relaxed max-w-2xl">
            Every story below is rooted in a concrete metric — reporting time saved, risks surfaced earlier, decisions defended. No vanity quotes.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <a
              href="/ARISSAI_Case_Studies.pdf"
              download="ARISSAI_Case_Studies.pdf"
              className="inline-flex items-center gap-2 h-11 px-5 bg-[#0020F5] text-white text-sm font-bold hover:bg-[#0018CC] transition-colors"
              data-testid="case-studies-download-pdf"
            >
              <FilePdf size={16} weight="fill" />
              Download the 2-page PDF
              <ArrowRight size={14} weight="bold" />
            </a>
            <a
              href={DEMO_BOOKING_URL}
              target={DEMO_USES_EXTERNAL ? "_blank" : undefined}
              rel={DEMO_USES_EXTERNAL ? "noopener noreferrer" : undefined}
              className="inline-flex items-center gap-2 h-11 px-5 bg-zinc-950 text-white text-sm font-bold hover:bg-zinc-800 transition-colors"
              data-testid="case-studies-book-demo"
            >
              <CalendarCheck size={16} weight="fill" />
              Book a 15-min demo
              <ArrowRight size={14} weight="bold" />
            </a>
            <span className="text-xs text-zinc-500 font-mono uppercase tracking-[0.18em]">
              // Read, share, then talk to us
            </span>
          </div>
        </div>
      </section>

      {/* Published case studies */}
      <main className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-24" data-testid="case-studies-list">
        <div className="space-y-12">
          {PUBLISHED_CASE_STUDIES.map((cs) => (
            <article
              key={cs.slug}
              className="bg-white border border-zinc-200 grid lg:grid-cols-12 gap-px"
              data-testid={`case-study-${cs.slug}`}
            >
              {/* Stat panel */}
              <div className="lg:col-span-4 bg-zinc-950 text-white p-8 sm:p-10 flex flex-col justify-between">
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold" style={{ color: cs.accent }}>// Headline result</div>
                  <div className="font-display font-black text-7xl sm:text-8xl mt-6 leading-none">
                    {cs.headline_stat.value}
                    <span className="text-3xl ml-2 font-bold" style={{ color: cs.accent }}>{cs.headline_stat.unit}</span>
                  </div>
                  <p className="text-sm text-zinc-300 mt-5 leading-relaxed">{cs.headline_stat.caption}</p>
                </div>
                <div className="mt-10 pt-6 border-t border-zinc-800 text-[10px] tracking-[0.18em] uppercase font-bold text-zinc-500 space-y-1">
                  <div>Sector · {cs.industry}</div>
                  <div>{cs.customer}</div>
                  <div>{cs.period}</div>
                </div>
              </div>

              {/* Story panel */}
              <div className="lg:col-span-8 p-8 sm:p-10">
                <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight leading-tight">{cs.title}</h2>

                <div className="mt-7 grid sm:grid-cols-2 gap-7">
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">// Challenge</div>
                    <p className="text-sm text-zinc-700 mt-2 leading-relaxed">{cs.challenge}</p>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">// What ARISSAI did</div>
                    <p className="text-sm text-zinc-700 mt-2 leading-relaxed">{cs.solution}</p>
                  </div>
                </div>

                <div className="mt-7">
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">// Results</div>
                  <ul className="mt-3 space-y-2">
                    {cs.results.map((r) => (
                      <li key={r} className="flex items-start gap-3 text-sm text-zinc-800 leading-relaxed">
                        <CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0" style={{ color: cs.accent }} />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <blockquote
                  className="mt-7 p-6 bg-zinc-100 border-l-4 relative"
                  style={{ borderLeftColor: cs.accent }}
                  data-testid={`case-study-quote-${cs.slug}`}
                >
                  <Quotes size={28} weight="fill" className="absolute top-4 left-4 opacity-15" style={{ color: cs.accent }} />
                  <p className="text-base sm:text-lg italic leading-relaxed text-zinc-900 pl-8">"{cs.quote}"</p>
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500 mt-4 pl-8">— {cs.attribution}</div>
                </blockquote>

                <Link
                  to={`/signup?industry=${cs.industrySlug}`}
                  className="mt-7 inline-flex items-center gap-2 h-11 px-5 text-sm font-bold text-white hover:opacity-90"
                  style={{ background: cs.accent }}
                  data-testid={`case-study-cta-${cs.slug}`}
                >
                  Run a {cs.industry.split(' ')[0]} project on ARISSAI <ArrowRight size={14} weight="bold" />
                </Link>
              </div>
            </article>
          ))}
        </div>

        {/* Coming soon strip */}
        {upcoming.length > 0 && (
          <div className="mt-20" data-testid="case-studies-coming-soon">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-5">// Coming soon</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
              {upcoming.map((cs) => (
                <div key={cs.slug} className="bg-white p-6 opacity-70" data-testid={`case-study-upcoming-${cs.slug}`}>
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold" style={{ color: cs.accent }}>{cs.industry}</div>
                  <div className="font-display font-bold text-base mt-3 leading-tight">{cs.title}</div>
                  <div className="text-[11px] tracking-[0.18em] uppercase font-bold text-zinc-400 mt-4">Targeting {cs.period}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Submit your story CTA */}
        <div className="mt-20 bg-zinc-950 text-white p-8 sm:p-10 grid lg:grid-cols-12 gap-8 items-center" data-testid="submit-story-cta">
          <div className="lg:col-span-8">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020F5]">// Built a win on ARISSAI?</div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl mt-3 leading-tight">Get featured in the next case study.</h2>
            <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
              We co-write the story, you keep approval rights, and your team gets a credit on the home page.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <a
              href="mailto:hello@arissai.com?subject=ARISSAI%20Case%20Study"
              className="inline-flex items-center gap-2 h-11 px-5 bg-white text-zinc-950 text-sm font-bold hover:bg-zinc-200"
              data-testid="submit-story-email"
            >
              hello@arissai.com <ArrowRight size={14} weight="bold" />
            </a>
          </div>
        </div>
      </main>

      <footer className="border-t border-zinc-200 bg-white py-10">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 flex items-center justify-between text-xs text-zinc-500 font-mono">
          <Mark />
          <div>© 2026 ARISSAI</div>
        </div>
      </footer>
    </div>
  );
}
